import {
  businessEntityKeys,
  ListGovernmentIdentifierExtensibleFields,
  queryClient,
  useBusinessEntityDocuments,
  useSupportedDocuments,
} from '@commenda/api';
import useGovernmentIdentifiers from '@commenda/api/government-identifiers/useGovernmentIdentifiers';
import { LoadingIndicator, useToggle } from '@commenda/kakashi';
import {
  BusinessEntityDocumentCategory,
  IGovernmentIdentifier,
} from '@commenda/types';
import { IDocument } from '@commenda/types/src/drive-view/types';
import {
  documentDescription,
  documentName,
  downloadFileFromFileId,
} from '@commenda/utils';
import { LegacyDocumentType } from '@prisma/client';
import { useState } from 'react';
import { DataPoint } from '../../../../common/DataPoint';
import { EditGovernmentIdentifierModal } from '../GovernmentIdentifiers/Modals/EditGovernmentIdentifierModal';
import { UploadFormationDocumentModal } from './UploadFormationDocumentModal';

export const FormationDocuments = ({
  businessEntityId,
  hideDescription = false,
  hideEditIcon = false,
  disabled = false,
  companyId,
}: {
  businessEntityId: number;
  hideDescription?: boolean;
  hideEditIcon?: boolean;
  disabled?: boolean;
  companyId: number;
}) => {
  const {
    data: documentsResponse,
    error: documentsError,
    mutate: mutateDocuments,
  } = useBusinessEntityDocuments({
    businessEntityId,
  });

  const { data: supportedDocumentsResponse, error: supportedDocumentsError } =
    useSupportedDocuments({
      businessEntityId,
    });

  const {
    data: governmentIdentifiersResponse,
    error: governmentIdentifiersError,
    mutate: mutateGovernmentIdentifiers,
  } = useGovernmentIdentifiers(businessEntityId, {
    extend: [ListGovernmentIdentifierExtensibleFields.SUPPORTING_DOCUMENT],
  });

  const uploadFormationDocumentModalVisibility = useToggle(false);
  const editGovernmentIdentifierModalVisibility = useToggle(false);

  const [selectedFormationDocument, setSelectedFormationDocument] =
    useState<IDocument | null>(null);
  const [selectedGovernmentIdentifier, setSelectedGovernmentIdentifier] =
    useState<IGovernmentIdentifier | null>(null);

  // Holds the subtype when we are creating a BRAND-NEW document ("Add new")
  const [newDocumentSubType, setNewDocumentSubType] =
    useState<LegacyDocumentType | null>(null);

  if (
    !documentsResponse ||
    !governmentIdentifiersResponse ||
    !supportedDocumentsResponse
  ) {
    if (documentsError) console.error(documentsError);
    if (governmentIdentifiersError) console.error(governmentIdentifiersError);
    if (supportedDocumentsError) console.error(supportedDocumentsError);

    return (
      <div className="flex flex-col items-center gap-2xl">
        <div className="flex flex-col gap-xs w-full">
          <h3 className="txt-large-bold">Formation documents</h3>
          <p className="txt-small-regular text-text-tertiary max-w-xl">
            Your core company formation documents essential for verifying your
            business structure.
          </p>
        </div>
        <LoadingIndicator _className="mx-auto" size={64} />
      </div>
    );
  }

  const formationDocuments =
    documentsResponse?.documents?.filter(
      (document) =>
        document.documentType ===
        BusinessEntityDocumentCategory.FORMATION_DOCUMENT
    ) || [];

  const documentRulesMap = new Map(
    supportedDocumentsResponse.supportedDocumentTypes
      .filter(
        (rule) =>
          rule.documentType ===
          BusinessEntityDocumentCategory.FORMATION_DOCUMENT
      )
      .map((rule) => [rule.documentSubType, rule])
  );

  // Prefer the content rule's per-country document name (e.g. "Public Deed
  // Extract" for Chile) over the generic enum label; fall back to the enum map.
  const documentLabel = (subType: LegacyDocumentType | string) =>
    documentRulesMap.get(subType as LegacyDocumentType)?.documentName ??
    documentName(subType as LegacyDocumentType);

  // Group docs by subtype for synthetic row logic
  const formationDocsBySubtype: Record<LegacyDocumentType, IDocument[]> =
    {} as Record<LegacyDocumentType, IDocument[]>;

  formationDocuments.forEach((doc) => {
    const key = doc.documentSubType as LegacyDocumentType;
    if (!formationDocsBySubtype[key]) formationDocsBySubtype[key] = [];
    formationDocsBySubtype[key].push(doc);
  });

  // Ensure deterministic order inside each subtype group (oldest first by createdAt or id)
  Object.values(formationDocsBySubtype).forEach((arr) =>
    arr.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return a.createdAt - b.createdAt;
      }
      return (a.id ?? 0) - (b.id ?? 0);
    })
  );

  if (formationDocuments.length === 0) return null;

  return (
    <div className="flex flex-col gap-3xl">
      <UploadFormationDocumentModal
        isOpen={uploadFormationDocumentModalVisibility.visible}
        onClose={uploadFormationDocumentModalVisibility.hide}
        title={
          selectedFormationDocument
            ? documentLabel(selectedFormationDocument.documentSubType)
            : `${documentLabel(newDocumentSubType)}`
        }
        onSuccess={async () => {
          await mutateGovernmentIdentifiers();
          await mutateDocuments();
          uploadFormationDocumentModalVisibility.hide();
          setSelectedFormationDocument(null);
          setSelectedGovernmentIdentifier(null);
        }}
        businessEntityId={businessEntityId}
        documentType={
          (selectedFormationDocument?.documentSubType ?? newDocumentSubType)!
        }
        existingDocument={selectedFormationDocument}
        companyId={companyId}
      />
      {selectedGovernmentIdentifier ? (
        <EditGovernmentIdentifierModal
          companyId={companyId}
          isOpen={editGovernmentIdentifierModalVisibility.visible}
          onClose={editGovernmentIdentifierModalVisibility.hide}
          businessEntityId={businessEntityId}
          governmentIdentifier={selectedGovernmentIdentifier}
          onSuccess={async () => {
            await queryClient.invalidateQueries({
              queryKey: [
                businessEntityKeys.FETCH_BUSINESS_ENTITY,
                +businessEntityId,
              ],
            });
            await mutateGovernmentIdentifiers();
            await mutateDocuments();
            setSelectedFormationDocument(null);
            editGovernmentIdentifierModalVisibility.hide();
            setSelectedGovernmentIdentifier(null);
          }}
          isEditable={false}
        />
      ) : null}
      <div className="flex flex-col gap-xs">
        <h3 className="txt-large-bold">Formation documents</h3>
        {!hideDescription && (
          <p className="txt-small-regular text-text-tertiary max-w-xl">
            Your core company formation documents essential for verifying your
            business structure.
          </p>
        )}
      </div>

      <div className="rounded-xl overflow-clip divide-y divide-border-primary">
        {Object.entries(formationDocsBySubtype).flatMap(([subtype, docs]) => {
          const actualUploadedDocs = docs.filter((d) => d.id);
          const subtypeDocsElements = docs.map((document) => (
            <div
              key={`${subtype}-${document.id || document.documentSubType}`}
              data-ai-item="Formation document"
              {...(document.id ? { 'data-ai-id': String(document.id) } : {})}
            >
              <DataPoint
                description={documentDescription(document.documentSubType)}
                title={
                  // If there are multiple of the same file, show them in order:
                  actualUploadedDocs.length > 1 && document.id
                    ? `${documentLabel(document.documentSubType)} (${actualUploadedDocs.findIndex((d) => d.id === document.id) + 1})`
                    : documentLabel(document.documentSubType)
                }
                value={document.fileName}
                document={{
                  id: +document.id,
                  isSupported: true,
                  fileUrl: document.fileUrl || null,
                  fileName: document.fileName || null,
                  onViewDocument: () =>
                    downloadFileFromFileId(+document.id, document.fileName),
                }}
                disabled={disabled}
                editConfig={
                  !hideEditIcon
                    ? {
                        isAllowed: true,
                        onEdit: () => {
                          // The identifier this file is actually filed against, not the
                          // first one whose accepted types happen to include its subtype —
                          // several identifiers accept a certificate of incorporation, and
                          // the edit opened on whichever the list returned first.
                          const existingGovernmentIdentifier =
                            governmentIdentifiersResponse.governmentIdentifiers?.find(
                              (identifier) =>
                                identifier.supportingDocuments.some(
                                  (supporting) =>
                                    supporting.id === Number(document.id)
                                )
                            );

                          if (existingGovernmentIdentifier) {
                            setSelectedGovernmentIdentifier(
                              existingGovernmentIdentifier
                            );
                            editGovernmentIdentifierModalVisibility.show();
                          } else {
                            setSelectedFormationDocument(document);
                            setNewDocumentSubType(null);
                            uploadFormationDocumentModalVisibility.show();
                          }
                        },
                      }
                    : undefined
                }
              />
            </div>
          ));

          // If non-unique, append synthetic "Add another" row
          const firstDoc = docs[0];
          const documentRule = documentRulesMap.get(
            firstDoc.documentSubType as LegacyDocumentType
          );
          const isUnique = documentRule?.isUnique ?? firstDoc.isUnique;

          if (!isUnique && actualUploadedDocs.length > 0) {
            subtypeDocsElements.push(
              <DataPoint
                key={`${subtype}-add-another`}
                description={documentDescription(firstDoc.documentSubType)}
                title={`Add another ${documentLabel(firstDoc.documentSubType)}`}
                value={null}
                disabled={disabled}
                editConfig={
                  !hideEditIcon
                    ? {
                        isAllowed: true,
                        onEdit: () => {
                          setSelectedFormationDocument(null);
                          setSelectedGovernmentIdentifier(null);
                          setNewDocumentSubType(
                            firstDoc.documentSubType as LegacyDocumentType
                          );
                          uploadFormationDocumentModalVisibility.show();
                        },
                      }
                    : undefined
                }
              />
            );
          }

          return subtypeDocsElements;
        })}
      </div>
    </div>
  );
};
